import { callEndpoint } from 'redux/global';
import { ManagerReport } from 'pages/Reporting/redux';

export type FormsOpenReportListItem = {
  id: string,
  toEmail: string,
  toName: string,
  fromEmail: string,
  fromName: string,
  subject: string,
  description: string,
  preview: string,
  createdAt: string,
};

/** Filed/dismissed reports still within the 7-day Forms archive window. */
export type FormsArchivedReportListItem = FormsOpenReportListItem & {
  reviewedAt: string,
  resolution: 'filed' | 'dismissed',
};

export type ReportRequestListItem = {
  id: string,
  fromEmail: string,
  fromName: string,
  toEmail: string,
  toName: string,
  subject: string,
  message: string,
  status: string,
  fulfilledReportId?: string,
  createdAt: string,
  updatedAt: string,
  isIncoming?: boolean,
  isOutgoing?: boolean,
};

export type FormsInboxCounts = {
  openReports: number,
  openReportRequests: number,
};

export const fetchFormsOpenReports = async (
  dispatch: Function,
): Promise<FormsOpenReportListItem[]> => {
  const res = await dispatch(callEndpoint({
    api: 'formsopenreports',
    method: 'GET',
  }));
  if (res?.status !== 'success' || !Array.isArray(res.data)) return [];
  return res.data as FormsOpenReportListItem[];
};

export const fetchFormsArchivedReports = async (
  dispatch: Function,
): Promise<FormsArchivedReportListItem[]> => {
  const res = await dispatch(callEndpoint({
    api: 'formsarchivedreports',
    method: 'GET',
  }));
  if (res?.status !== 'success' || !Array.isArray(res.data)) return [];
  return res.data as FormsArchivedReportListItem[];
};

export const fetchReportRequests = async (
  dispatch: Function,
): Promise<ReportRequestListItem[]> => {
  const res = await dispatch(callEndpoint({
    api: 'reportrequests',
    method: 'GET',
  }));
  if (res?.status !== 'success' || !Array.isArray(res.data)) return [];
  return res.data as ReportRequestListItem[];
};

export const fetchFormsInboxCounts = async (
  dispatch: Function,
): Promise<FormsInboxCounts> => {
  const res = await dispatch(callEndpoint({
    api: 'formsinboxcounts',
    method: 'GET',
  }));
  if (res?.status !== 'success' || !res.data || typeof res.data !== 'object') {
    return { openReports: 0, openReportRequests: 0 };
  }
  const row = res.data as FormsInboxCounts;
  return {
    openReports: Number(row.openReports) || 0,
    openReportRequests: Number(row.openReportRequests) || 0,
  };
};

export const cancelReportRequest = (id: string) => async (dispatch: Function) => dispatch(callEndpoint({
  api: `reportrequest/${id}/cancel`,
  method: 'POST',
  body: {},
}));

/** Map forms open/archived report row to ManagerReport shape for the viewer. */
export const formsOpenReportAsManagerReport = (
  item: FormsOpenReportListItem | FormsArchivedReportListItem,
): ManagerReport => ({
  id: item.id,
  toEmail: item.toEmail,
  toName: item.toName,
  fromEmail: item.fromEmail,
  fromName: item.fromName,
  subject: item.subject,
  description: item.description,
  preview: item.preview || item.subject,
  pdfGcsPath: '',
  createdAt: item.createdAt,
  reviewedAt: 'reviewedAt' in item ? item.reviewedAt : null,
  resolution: 'resolution' in item ? item.resolution : null,
});
